/**
 * Admin API hooks for Settings page sections.
 * Each list query tolerates 404/405 by returning { kind: "unavailable" }
 * so the UI can render a "Coming online" placeholder instead of an error.
 * Mutations toast on the same condition and otherwise propagate.
 *
 * Spec: user-uploads/LOVABLE_SETTINGS_PAGE.md
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  edrGet,
  edrPost,
  edrPatch,
  edrPut,
  edrDelete,
  isMissingRoute,
} from "@/services/edrApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";

// ---------- shared helpers ----------

type AvailResult<T> = { kind: "ok"; data: T } | { kind: "unavailable" };

function useGated<T>(
  key: readonly unknown[],
  path: string,
  opts: { staleMs?: number } = {},
) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: key,
    staleTime: opts.staleMs ?? 15_000,
    queryFn: async (): Promise<AvailResult<T>> => {
      try {
        const data = await edrGet<T>(path);
        return { kind: "ok", data };
      } catch (err) {
        if (isMissingRoute(err)) return { kind: "unavailable" };
        throw err;
      }
    },
  });
}

function handleMutErr(err: unknown, friendly: string) {
  if (isMissingRoute(err)) {
    toast.message("Endpoint not yet available", {
      description: "This admin endpoint hasn't shipped yet.",
    });
    return;
  }
  toast.error(err instanceof Error ? err.message : friendly);
}

// ============================================================
// Section 1 — General (tenant + retention)
// ============================================================

export interface TenantDTO {
  id: string;
  name: string;
  createdAt: string;
  plan: string;
  trialDaysLeft?: number;
  sensorLimit: number;
  sensorCount: number;
}

export interface RetentionDTO {
  eventRetentionDays: number;
  alertRetentionDays: number;
  auditRetentionDays: number;
  snapshotRetentionDays: number;
}

export function useTenant() {
  return useGated<TenantDTO>(["edr", "admin", "tenant"], "/api/v1/connectors/edr/admin/tenant");
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      edrPut<TenantDTO>("/api/v1/connectors/edr/admin/tenant", input),
    onSuccess: () => {
      toast.success("Tenant updated");
      qc.invalidateQueries({ queryKey: ["edr", "admin", "tenant"] });
    },
    onError: (err) => handleMutErr(err, "Failed to update tenant"),
  });
}

export function useRetention() {
  return useGated<RetentionDTO>(
    ["edr", "admin", "retention"],
    "/api/v1/connectors/edr/retention",
  );
}

export function useUpdateRetention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<RetentionDTO>) =>
      edrPut<RetentionDTO>("/api/v1/connectors/edr/retention", input),
    onSuccess: () => {
      toast.success("Retention updated");
      qc.invalidateQueries({ queryKey: ["edr", "admin", "retention"] });
    },
    onError: (err) => handleMutErr(err, "Failed to update retention"),
  });
}

// ============================================================
// Section 3 — Users & Roles
// ============================================================

export type MfaMethod = "totp" | "webauthn" | "none";

export interface AdminUser {
  id: string;
  email: string;
  displayName?: string;
  roleId: string;
  roleName: string;
  mfaMethod: MfaMethod;
  lastSeenAt?: string;
  invitedAt?: string;
  invitedBy?: string;
}

export interface AdminRole {
  id: string;
  name: string;
  description?: string;
  permissions: string[];
  builtIn: boolean;
  userCount: number;
}

const USERS_KEY = ["edr", "admin", "users"] as const;
const ROLES_KEY = ["edr", "admin", "roles"] as const;

export function useAdminUsers() {
  return useGated<{ users: AdminUser[] }>(USERS_KEY, "/api/v1/connectors/edr/admin/users");
}
export function useAdminRoles() {
  return useGated<{ roles: AdminRole[] }>(ROLES_KEY, "/api/v1/connectors/edr/admin/roles");
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; roleId: string; sendEmail: boolean }) =>
      edrPost<AdminUser>("/api/v1/connectors/edr/admin/users", input),
    onSuccess: () => {
      toast.success("Invite sent");
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to invite user"),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; roleId?: string; mfaMethod?: MfaMethod }) =>
      edrPatch<AdminUser>(`/api/v1/connectors/edr/admin/users/${id}`, body),
    onSuccess: () => {
      toast.success("User updated");
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to update user"),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      edrDelete(`/api/v1/connectors/edr/admin/users/${id}`),
    onSuccess: () => {
      toast.success("User removed");
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to remove user"),
  });
}

// ============================================================
// Section 4 — SSO
// ============================================================

export interface SsoProvider {
  id: string;
  kind: "oidc" | "saml";
  name: string;
  issuer?: string;
  clientId?: string;
  redirectUri?: string;
  groupClaim?: string;
  groupMap?: Record<string, string>;
  enabled: boolean;
  lastLoginAt?: string;
  metadataUrl?: string;
}

export interface ScimStatus {
  enabled: boolean;
  endpoint?: string;
  lastSyncAt?: string;
  tokenHint?: string;
}

const SSO_KEY = ["edr", "admin", "sso"] as const;
const SCIM_KEY = ["edr", "admin", "scim"] as const;

export function useSsoProviders() {
  return useGated<{ providers: SsoProvider[] }>(
    SSO_KEY,
    "/api/v1/connectors/edr/admin/sso/providers",
  );
}

export function useScimStatus() {
  return useGated<ScimStatus>(SCIM_KEY, "/api/v1/connectors/edr/admin/scim/status");
}

export function useCreateSsoProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<SsoProvider>) =>
      edrPost<SsoProvider>("/api/v1/connectors/edr/admin/sso/providers", input),
    onSuccess: () => {
      toast.success("SSO provider added");
      qc.invalidateQueries({ queryKey: SSO_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to add provider"),
  });
}

export function useUpdateSsoProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<SsoProvider>) =>
      edrPatch<SsoProvider>(`/api/v1/connectors/edr/admin/sso/providers/${id}`, body),
    onSuccess: () => {
      toast.success("Provider updated");
      qc.invalidateQueries({ queryKey: SSO_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to update provider"),
  });
}

export function useDeleteSsoProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      edrDelete(`/api/v1/connectors/edr/admin/sso/providers/${id}`),
    onSuccess: () => {
      toast.success("Provider removed");
      qc.invalidateQueries({ queryKey: SSO_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to remove provider"),
  });
}

export function useTestSsoProvider() {
  return useMutation({
    mutationFn: (id: string) =>
      edrPost<{ ok: boolean; message?: string }>(
        `/api/v1/connectors/edr/admin/sso/providers/${id}/test`,
      ),
    onSuccess: (r) => toast.success(r.ok ? "Test login succeeded" : r.message || "Test ran"),
    onError: (err) => handleMutErr(err, "Test failed"),
  });
}

export function useEnableScim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      edrPost<ScimStatus>("/api/v1/connectors/edr/admin/scim/enable", { enabled }),
    onSuccess: () => {
      toast.success("SCIM updated");
      qc.invalidateQueries({ queryKey: SCIM_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to update SCIM"),
  });
}

export function useRotateScimToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      edrPost<{ token: string }>("/api/v1/connectors/edr/admin/scim/rotate-token"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SCIM_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to rotate token"),
  });
}

// ============================================================
// Section 5 — API Tokens
// ============================================================

export interface ApiToken {
  id: string;
  name: string;
  scopes: string[];
  createdAt: string;
  createdBy?: string;
  lastUsedAt?: string;
  lastUsedIP?: string;
  expiresAt?: string | null;
}

export interface ApiTokenCreated extends ApiToken {
  /** Plaintext token returned exactly once. */
  token: string;
}

const TOKENS_KEY = ["edr", "admin", "api-tokens"] as const;

export function useApiTokens() {
  return useGated<{ tokens: ApiToken[] }>(TOKENS_KEY, "/api/v1/connectors/edr/admin/api-tokens");
}

export function useCreateApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; scopes: string[]; expiresInDays?: number | null }) =>
      edrPost<ApiTokenCreated>("/api/v1/connectors/edr/admin/api-tokens", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TOKENS_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to create token"),
  });
}

export function useRevokeApiToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      edrDelete(`/api/v1/connectors/edr/admin/api-tokens/${id}`),
    onSuccess: () => {
      toast.success("Token revoked");
      qc.invalidateQueries({ queryKey: TOKENS_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to revoke token"),
  });
}

// ============================================================
// Section 6 — Webhooks
// ============================================================

export interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  enabled: boolean;
  lastDeliveryAt?: string;
  lastDeliveryStatus?: number;
  lastDeliveryOk?: boolean;
  secretHint?: string;
}

export interface WebhookDelivery {
  id: string;
  ts: string;
  event: string;
  responseStatus: number;
  latencyMs: number;
  retryCount: number;
  errorMessage?: string;
}

const WEBHOOKS_KEY = ["edr", "admin", "webhooks"] as const;

export function useWebhooks() {
  return useGated<{ webhooks: Webhook[] }>(WEBHOOKS_KEY, "/api/v1/connectors/edr/admin/webhooks");
}

export function useWebhookDeliveries(id: string | null) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled: enabled && !!id,
    queryKey: ["edr", "admin", "webhooks", id, "deliveries"],
    queryFn: async () => {
      try {
        return await edrGet<{ deliveries: WebhookDelivery[] }>(
          `/api/v1/connectors/edr/admin/webhooks/${id}/deliveries`,
        );
      } catch (err) {
        if (isMissingRoute(err)) return { deliveries: [] };
        throw err;
      }
    },
  });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; url: string; secret?: string; events: string[] }) =>
      edrPost<Webhook>("/api/v1/connectors/edr/admin/webhooks", input),
    onSuccess: () => {
      toast.success("Webhook added");
      qc.invalidateQueries({ queryKey: WEBHOOKS_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to add webhook"),
  });
}

export function useUpdateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<Webhook>) =>
      edrPatch<Webhook>(`/api/v1/connectors/edr/admin/webhooks/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: WEBHOOKS_KEY }),
    onError: (err) => handleMutErr(err, "Failed to update webhook"),
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => edrDelete(`/api/v1/connectors/edr/admin/webhooks/${id}`),
    onSuccess: () => {
      toast.success("Webhook removed");
      qc.invalidateQueries({ queryKey: WEBHOOKS_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to remove webhook"),
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: (id: string) =>
      edrPost<{ ok: boolean; responseStatus?: number }>(
        `/api/v1/connectors/edr/admin/webhooks/${id}/test`,
      ),
    onSuccess: (r) => toast.success(`Test sent · ${r.responseStatus ?? "ok"}`),
    onError: (err) => handleMutErr(err, "Test failed"),
  });
}

export function useReplayDelivery() {
  return useMutation({
    mutationFn: ({ id, deliveryId }: { id: string; deliveryId: string }) =>
      edrPost<{ ok: boolean }>(
        `/api/v1/connectors/edr/admin/webhooks/${id}/deliveries/${deliveryId}/replay`,
      ),
    onSuccess: () => toast.success("Replayed"),
    onError: (err) => handleMutErr(err, "Replay failed"),
  });
}

// ============================================================
// Section 7 — SIEM Forwarding
// ============================================================

export type SiemKind = "splunk_hec" | "datadog" | "sumo" | "https" | "s3";

export interface SiemDestination {
  id: string;
  name: string;
  kind: SiemKind;
  endpoint: string;
  index?: string;
  format: "ocsf" | "cef" | "json";
  forwardAlerts: boolean;
  forwardEvents: boolean;
  forwardAudit: boolean;
  forwardClassifications: boolean;
  throughputCapEps?: number;
  lastFlushAt?: string;
  lagSeconds?: number;
}

const SIEM_KEY = ["edr", "admin", "siem"] as const;

export function useSiemDestinations() {
  return useGated<{ destinations: SiemDestination[] }>(
    SIEM_KEY,
    "/api/v1/connectors/edr/admin/siem-destinations",
  );
}

export function useCreateSiem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<SiemDestination> & { token?: string }) =>
      edrPost<SiemDestination>("/api/v1/connectors/edr/admin/siem-destinations", input),
    onSuccess: () => {
      toast.success("Destination added");
      qc.invalidateQueries({ queryKey: SIEM_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to add destination"),
  });
}

export function useUpdateSiem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<SiemDestination>) =>
      edrPatch<SiemDestination>(`/api/v1/connectors/edr/admin/siem-destinations/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: SIEM_KEY }),
    onError: (err) => handleMutErr(err, "Failed to update destination"),
  });
}

export function useDeleteSiem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      edrDelete(`/api/v1/connectors/edr/admin/siem-destinations/${id}`),
    onSuccess: () => {
      toast.success("Destination removed");
      qc.invalidateQueries({ queryKey: SIEM_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to remove destination"),
  });
}

export function useTestSiem() {
  return useMutation({
    mutationFn: (id: string) =>
      edrPost<{ ok: boolean; message?: string }>(
        `/api/v1/connectors/edr/admin/siem-destinations/${id}/test`,
      ),
    onSuccess: (r) => toast.success(r.ok ? "Test event sent" : r.message || "Test ran"),
    onError: (err) => handleMutErr(err, "Test failed"),
  });
}

// ============================================================
// Section 8 — Notifications
// ============================================================

export type NotifKind = "slack" | "email" | "pagerduty" | "webhook";

export interface NotifChannel {
  id: string;
  kind: NotifKind;
  name: string;
  target: string;
  severityMin: "info" | "low" | "medium" | "high" | "critical";
  quietHours?: { start: string; end: string } | null;
}

export interface NotifRoute {
  id: string;
  description: string;
  match: Record<string, unknown>;
  channelIds: string[];
}

const NCHAN_KEY = ["edr", "admin", "notif-channels"] as const;
const NROUTE_KEY = ["edr", "admin", "notif-routes"] as const;

export function useNotifChannels() {
  return useGated<{ channels: NotifChannel[] }>(
    NCHAN_KEY,
    "/api/v1/connectors/edr/admin/notification-channels",
  );
}

export function useNotifRoutes() {
  return useGated<{ routes: NotifRoute[] }>(
    NROUTE_KEY,
    "/api/v1/connectors/edr/admin/notification-routes",
  );
}

export function useCreateNotifChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<NotifChannel>) =>
      edrPost<NotifChannel>("/api/v1/connectors/edr/admin/notification-channels", input),
    onSuccess: () => {
      toast.success("Channel added");
      qc.invalidateQueries({ queryKey: NCHAN_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to add channel"),
  });
}

export function useDeleteNotifChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      edrDelete(`/api/v1/connectors/edr/admin/notification-channels/${id}`),
    onSuccess: () => {
      toast.success("Channel removed");
      qc.invalidateQueries({ queryKey: NCHAN_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to remove channel"),
  });
}

export function useTestNotifChannel() {
  return useMutation({
    mutationFn: (id: string) =>
      edrPost<{ ok: boolean }>(
        `/api/v1/connectors/edr/admin/notification-channels/${id}/test`,
      ),
    onSuccess: () => toast.success("Test sent"),
    onError: (err) => handleMutErr(err, "Test failed"),
  });
}

// ============================================================
// Section 9 — External Sources
// ============================================================

export interface ExternalSource {
  id: string;
  name: string;
  kind: string;
  enabled: boolean;
  createdAt: string;
  lastEventAt?: string;
}

const EXTSRC_KEY = ["edr", "admin", "external-sources"] as const;

export function useExternalSources() {
  return useGated<{ sources: ExternalSource[] }>(
    EXTSRC_KEY,
    "/api/v1/connectors/edr/admin/external/sources",
  );
}

export function useCreateExternalSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; kind: string }) =>
      edrPost<ExternalSource & { token: string }>(
        "/api/v1/connectors/edr/admin/external/sources",
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: EXTSRC_KEY }),
    onError: (err) => handleMutErr(err, "Failed to add source"),
  });
}

export function useDeleteExternalSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      edrDelete(`/api/v1/connectors/edr/admin/external/sources/${id}`),
    onSuccess: () => {
      toast.success("Source removed");
      qc.invalidateQueries({ queryKey: EXTSRC_KEY });
    },
    onError: (err) => handleMutErr(err, "Failed to remove source"),
  });
}

// ============================================================
// Section 10 — Audit Log
// ============================================================

export interface AuditEntryDTO {
  id: string;
  ts: string;
  actor: string;
  actorType: "user" | "system" | "api_token" | "webhook";
  action: string;
  resourceType: string;
  resourceId: string;
  tenantId?: string;
  sourceIp?: string;
  requestId?: string;
  before?: unknown;
  after?: unknown;
  reason?: string;
}

export interface AuditFilters {
  since?: string;
  until?: string;
  actor?: string;
  action?: string;
  resource?: string;
  limit?: number;
  cursor?: string;
}

export function useAuditLog(filters: AuditFilters) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: ["edr", "admin", "audit-log", filters],
    staleTime: 5_000,
    queryFn: async () => {
      try {
        const sp = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
          if (v !== undefined && v !== "") sp.append(k, String(v));
        });
        const qs = sp.toString();
        return await edrGet<{
          entries: AuditEntryDTO[];
          total: number;
          nextCursor?: string;
        }>(`/api/v1/connectors/edr/admin/audit-log${qs ? `?${qs}` : ""}`);
      } catch (err) {
        if (isMissingRoute(err)) return { entries: [], total: 0 };
        throw err;
      }
    },
  });
}

export function auditExportUrl(format: "csv" | "json", filters: AuditFilters): string {
  const base =
    (import.meta.env.VITE_EDR_BACKEND_URL as string | undefined) ||
    "https://edr-demo.smplify.com";
  const sp = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== "") sp.append(k, String(v));
  });
  const qs = sp.toString();
  return `${base}/api/v1/connectors/edr/admin/audit-log/export.${format}${qs ? `?${qs}` : ""}`;
}
